import React, { FC, useMemo, useState, useEffect } from 'react';
import DiscountBlock from './components/discountBlock';
import PaymentCard from './components/paymentCard';
import TermsOfUse from './components/terms';
import s from './index.module.scss';
import GetPlanButton from './components/getPlanButton/GetPlanButton';
import ComposeStripeViewModel, { IStripeViewModel } from './viewModel';
import Delimiter from './components/delimiter';
import Paypal from '../../paypalV5';
import AppleGooglePayment from '../../appleGooglePaymentV2';
import { loadStripe } from '@stripe/stripe-js';
import errorLogger from '../../../../utils/errorLogger/appErrorLogger';
import APIServices from '../../../../utils/services/EveryDoggyAPIServices';
import { Elements } from '@stripe/react-stripe-js';
import { IPaymentModalProduct } from '../../../../reduxStore/data/state';
import { RootState } from '../../../../store';
import { useSelector } from 'react-redux';
import { getParameterFromURL } from '../../../../utils/scripts';
import locales from '../../../../content.json';
import { StripeElementsOptions } from '@stripe/stripe-js';

import applePayIcn from './img/applePayIcn.png';
import applePayIcnDark from './img/applePayIcnDark.png';
import payPalIcon from './img/payPalIcon.png';
import gpl from './img/gpl.png';
import gplDark from './img/gplDark.png';
import cardsIcon from './img/cardsIcn.png';
import secure from './img/srcure.png';
import stripe from './img/stripe.png';

type Props = {
  product: IPaymentModalProduct;
};

const Checkout: FC<Props> = props => {
  const { product } = props;
  const stripePlanId = product.details ? product.details.stripe_plan_id : '';
  const internalId = product.details ? product.details.internal_product_id : '';
  const [activeBlock, setActiveBlock] = useState(0);
  const [applePayShow, setApplePayShow] = useState(true);
  const darkTheme = getParameterFromURL('darkTheme') === 'true';

  const viewModel: IStripeViewModel = ComposeStripeViewModel(
    window.location.href,
    stripePlanId,
    APIServices,
    errorLogger,
    internalId
  );

  const [stripeError, setStripeError] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [cardEmail, setCardEmail] = useState(viewModel.initialEmail());
  const [appleGooglepayActive, setAppleGooglepayActive] = useState(true);
  const noPaypal = useSelector((state: RootState) => state.data.noPaypal);

  const orders = useMemo(() => {
    return {
      pm1: 2,
      pm2: 1,
      pm3: 3,
    };
  }, []);

  const firstOrder = Math.min(orders.pm1, orders.pm2, orders.pm3);

  const stylePM1 = {
    order: orders.pm1,
    marginTop: orders.pm1 === firstOrder ? 0 : undefined,
  };
  const stylePM2 = {
    order: orders.pm2,
    marginTop: orders.pm2 === firstOrder ? 0 : undefined,
    display: appleGooglepayActive ? 'block' : 'none',
  };
  const stylePM3 = {
    order: orders.pm3,
    marginTop: orders.pm3 === firstOrder ? 0 : undefined,
    display: !noPaypal ? 'block' : 'none',
  };

  const stripePromise = useMemo(() => {
    return viewModel.getStripeKey().then(stripeKey => loadStripe(stripeKey)).catch(() => null);
  }, []);

  const handleCardChange = (value: boolean, email: string) => {
    setDisableButton(!value);
    setCardEmail(email);
  };

  const handleToggle = (index: number) => {

    const element = document.getElementById('scrollContent');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    
    if (activeBlock !== index) {
      setActiveBlock(index);
    }
  };

  const title1 = useMemo(() => {
    if (applePayShow) {
      return 'Apple Pay';
    } else {
      return 'Google Pay';
    }
  }, [applePayShow]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (window.paymentRequestApplePayValue) {
        setAppleGooglepayActive(true);
      } else if (!window.paymentRequestApplePayValue) {
        setAppleGooglepayActive(false);
      }
      if (!window.paymentApplePay) {
        setApplePayShow(false);
      }
      if (window.paymentApplePay) {
        setApplePayShow(true);
      }
    }, 300);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (applePayShow || appleGooglepayActive) {
      setActiveBlock(1);
    } else {
      setActiveBlock(0);
    }

  }, [appleGooglepayActive, applePayShow]);

  const locale = navigator.language.startsWith('ja') || navigator.language.startsWith('ko') ? 'en' : navigator.language.slice(0, 2);
  const options: StripeElementsOptions = {
    locale: locale as StripeElementsOptions['locale'],
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className={s.сheckoutWrapper}>
        <div className={s.сheckoutWrapper_paymentMethods}>

          <div
            className={`${s.сheckoutWrapper_paymentMethod} ${s.сheckoutWrapper_paymentMethod1} ${
              activeBlock === 0 ? s.active : ''
            }`}
            style={stylePM1}
          >
            <div className={s.header} onClick={() => handleToggle(0)}>
              <input
                type="radio"
                id="radio-0"
                name="paymentMethod"
                checked={activeBlock === 0}
                onChange={() => handleToggle(0)}
                className={s.radio}
              />
              <label htmlFor="radio-0" className={s.radioLabel}>
                <p>{locales[window.userLang].payment_text_105}</p>
                <img className={s.cardsIcon} src={cardsIcon} alt="credit cards" />
              </label>
            </div>
            <div className={`${s.content} ${activeBlock === 0 ? s.active : ''}`}>
              <PaymentCard onCardCompleted={handleCardChange} />
              <GetPlanButton
                email={cardEmail}
                viewModel={viewModel}
                disabled={disableButton}
                setError={setStripeError}
              />
              {stripeError && <div className={s.error}>{stripeError}</div>}
            </div>
          </div>

          <div
            className={`${s.сheckoutWrapper_paymentMethod} ${s.сheckoutWrapper_paymentMethod2} ${
              activeBlock === 1 ? s.active : ''
            }`}
            style={stylePM2}
          >
            <div className={s.header} onClick={() => handleToggle(1)}>
              <input
                type="radio"
                id="radio-1"
                name="paymentMethod"
                checked={activeBlock === 1}
                onChange={() => handleToggle(1)}
                className={s.radio}
              />
              <label htmlFor="radio-1" className={s.radioLabel}>
                <p>{title1}</p>
                <img className={`${s.applePayIcn} ${!applePayShow ? s.gpl : ''}`} src={darkTheme ? (applePayShow ? applePayIcnDark : gplDark) : (applePayShow ? applePayIcn : gpl)} alt={title1} />
              </label>
            </div>
            <div className={`${s.content} ${activeBlock === 1 ? s.active : ''}`}>
              {applePayShow ? (
                <ul className={s.infoList}>
                  <li>
                    <div className={s.check} />
                    <p>{locales[window.userLang].checkoutV2_text2}</p>
                  </li>
                  <li>
                    <div className={s.check} />
                    <p>{locales[window.userLang].checkoutV2_text3}</p>
                  </li>
                  <li>
                    <div className={s.check} />
                    <p>{locales[window.userLang].checkoutV2_text4}</p>
                  </li>
                </ul>
              ) : (
                <ul className={s.infoList}>
                  <li>
                    <div className={s.check} />
                    <p>{locales[window.userLang].checkoutV2_text3}</p>
                  </li>
                </ul>
              )}

              <div className={`${s.buttonwrapper} ${s.applepay}`}>
                <AppleGooglePayment product={product} />
              </div>

              <div className={s.secure} />
              <div className={`${s.paypalWarnings} applepayWarnings`}></div>
            </div>
          </div>

          <div
            className={`${s.сheckoutWrapper_paymentMethod} ${s.сheckoutWrapper_paymentMethod3} ${
              activeBlock === 2 ? s.active : ''
            }`}
            style={stylePM3}
          >
            <div className={s.header} onClick={() => handleToggle(2)}>
              <input
                type="radio"
                id="radio-2"
                name="paymentMethod"
                checked={activeBlock === 2}
                onChange={() => handleToggle(2)}
                className={s.radio}
              />
              <label htmlFor="radio-2" className={s.radioLabel}>
                <p>PayPal</p>
                <img className={s.payPalIcon} src={payPalIcon} alt="payPal" />
              </label>
            </div>
            <div className={`${s.content} ${activeBlock === 2 ? s.active : ''}`}>
              <ul className={s.infoList}>
                <li>
                  <div className={s.check} />
                  <p>{locales[window.userLang].checkoutV2_text5}</p>
                </li>
                <li>
                  <div className={s.check} />
                  <p>{locales[window.userLang].checkoutV2_text6}</p>
                </li>
                <li>
                  <div className={s.check} />
                  <p>{locales[window.userLang].checkoutV2_text7}</p>
                </li>
              </ul>
              <div className={`${s.buttonwrapper} ${s.buttonWrapperPaypal}`}>
                <Paypal />
              </div>
              <div className={`${s.paypalWarnings} paypalWarnings`}></div>
            </div>
          </div>

        </div>
      </div>
    </Elements>
  );
};

export default Checkout;
